import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { talentService, userService } from '../../api/apiService';
import { customStyles } from '../../helpers/StyleUtils';

const TalentRelationshipView = () => {
  const [talents, setTalents] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedTalents, setExpandedTalents] = useState(new Set());
  const [talentRelationships, setTalentRelationships] = useState({});
  const [companyRelationships, setCompanyRelationships] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch all talents
      const talentsResponse = await talentService.getAllTalents(1, 5000);
      const talentsData = talentsResponse.data?.talents || [];
      setTalents(talentsData);

      // Fetch all users with talents
      const usersResponse = await userService.getAllUsersWithTalents();
      const usersData = usersResponse.data?.users || [];
      setUsers(usersData);

      // Fetch relationships for each talent
      await fetchTalentRelationships(talentsData);
      await fetchCompanyRelationships(talentsData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTalentRelationships = async (talentsData) => {
    const relationships = {};
    
    for (const talent of talentsData) {
      try {
        // This would need a backend endpoint to get talent relationships
        // For now, we'll show what we can infer from the data
        relationships[talent.id] = {
          linkedTalents: [], // Would come from backend
          userAssignments: []
        };
      } catch (error) {
        console.error(`Error fetching relationships for talent ${talent.id}:`, error);
      }
    }
    
    setTalentRelationships(relationships);
  };

  const fetchCompanyRelationships = async (talentsData) => {
    const relationships = {};
    
    for (const talent of talentsData) {
      try {
        const response = await talentService.getTalentCompanies(talent.id);
        if (response.data) {
          relationships[talent.id] = {
            goodPerformance: response.data.good_performance_companies || [],
            badPerformance: response.data.bad_performance_companies || [],
            tryout: response.data.tryout_companies || []
          };
        }
      } catch (error) {
        console.error(`Error fetching company relationships for talent ${talent.id}:`, error);
        relationships[talent.id] = {
          goodPerformance: [],
          badPerformance: [],
          tryout: []
        };
      }
    }
    
    setCompanyRelationships(relationships);
  };

  const toggleExpanded = (talentId) => {
    const newExpanded = new Set(expandedTalents);
    if (newExpanded.has(talentId)) {
      newExpanded.delete(talentId);
    } else {
      newExpanded.add(talentId);
    }
    setExpandedTalents(newExpanded);
  };

  const getUsersForTalent = (talentId) => {
    return users.filter(user => 
      user.talents && user.talents.some(talent => talent.id === talentId)
    );
  };

  const getFunctionOptions = () => {
    const functions = [...new Set(talents.map(talent => talent.function || talent.Funktion).filter(Boolean))];
    return functions.map(func => ({ value: func, label: func }));
  };

  const getLocationOptions = () => {
    const locations = [...new Set(talents.map(talent => talent.location).filter(Boolean))];
    return locations.map(location => ({ value: location, label: location }));
  };

  const getStatusOptions = () => {
    const statuses = [...new Set(talents.map(talent => talent.status).filter(Boolean))];
    return statuses.map(status => ({ value: status, label: status }));
  };

  const filteredTalents = talents.filter(talent => {
    const matchesSearch = !searchTerm || 
      (talent.name && talent.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (talent.function && talent.function.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (talent.location && talent.location.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFunction = !selectedFunction || talent.function === selectedFunction.value;
    const matchesLocation = !selectedLocation || talent.location === selectedLocation.value;
    const matchesStatus = !selectedStatus || talent.status === selectedStatus.value;

    return matchesSearch && matchesFunction && matchesLocation && matchesStatus;
  });

  const renderCompanyRelationships = (talentId) => {
    const relationships = companyRelationships[talentId] || {};
    
    return (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Company Relationships:</h4>
        
        {relationships.goodPerformance && relationships.goodPerformance.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Good Performance:</span>
            <div style={{ marginLeft: '15px', fontSize: '12px' }}>
              {relationships.goodPerformance.map((company, index) => (
                <div key={index}>• {company.name || company.label}</div>
              ))}
            </div>
          </div>
        )}
        
        {relationships.badPerformance && relationships.badPerformance.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗ Bad Performance:</span>
            <div style={{ marginLeft: '15px', fontSize: '12px' }}>
              {relationships.badPerformance.map((company, index) => (
                <div key={index}>• {company.name || company.label}</div>
              ))}
            </div>
          </div>
        )}
        
        {relationships.tryout && relationships.tryout.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: '#ffc107', fontWeight: 'bold' }}>? Tryout:</span>
            <div style={{ marginLeft: '15px', fontSize: '12px' }}>
              {relationships.tryout.map((company, index) => (
                <div key={index}>• {company.name || company.label}</div>
              ))}
            </div>
          </div>
        )}
        
        {(!relationships.goodPerformance || relationships.goodPerformance.length === 0) &&
         (!relationships.badPerformance || relationships.badPerformance.length === 0) &&
         (!relationships.tryout || relationships.tryout.length === 0) && (
          <div style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '12px' }}>
            No company relationships found
          </div>
        )}
      </div>
    );
  };

  const renderUserAssignments = (talentId) => {
    const assignedUsers = getUsersForTalent(talentId);
    
    return (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>User Assignments:</h4>
        
        {assignedUsers.length > 0 ? (
          assignedUsers.map((user, index) => (
            <div key={index} style={{ marginBottom: '5px', fontSize: '12px' }}>
              <span style={{ fontWeight: 'bold' }}>👤 {user.email}</span>
              <span style={{ color: '#666', marginLeft: '10px' }}>({user.role})</span>
            </div>
          ))
        ) : (
          <div style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '12px' }}>
            No users assigned to this talent
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1 style={{ marginBottom: '30px', color: '#333' }}>Talent Relationship View</h1>
      
      {/* Filters */}
      <div style={{ marginBottom: '30px', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Search talents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              width: '100%'
            }}
          />
        </div>
        
        <div style={{ minWidth: '150px' }}>
          <Select
            value={selectedFunction}
            onChange={setSelectedFunction}
            options={getFunctionOptions()}
            placeholder="Filter by Function"
            isClearable
            styles={customStyles}
          />
        </div>
        
        <div style={{ minWidth: '150px' }}>
          <Select
            value={selectedLocation}
            onChange={setSelectedLocation}
            options={getLocationOptions()}
            placeholder="Filter by Location"
            isClearable
            styles={customStyles}
          />
        </div>
        
        <div style={{ minWidth: '150px' }}>
          <Select
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={getStatusOptions()}
            placeholder="Filter by Status"
            isClearable
            styles={customStyles}
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginBottom: '30px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <div>
          <strong>Total Talents:</strong> {talents.length}
        </div>
        <div>
          <strong>Filtered:</strong> {filteredTalents.length}
        </div>
        <div>
          <strong>Total Users:</strong> {users.length}
        </div>
        <div>
          <strong>Users with Talents:</strong> {users.filter(user => user.talents && user.talents.length > 0).length}
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner"></div>
          <p>Loading talent relationships...</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filteredTalents.map((talent) => {
            const isExpanded = expandedTalents.has(talent.id);
            const assignedUsers = getUsersForTalent(talent.id);
            
            return (
              <div 
                key={talent.id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => toggleExpanded(talent.id)}
              >
                {/* Main Talent Info */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: 'bold' }}>
                      {talent.name || talent.Name || `Talent ${talent.id}`}
                    </h3>
                    <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: '#666' }}>
                      <span><strong>Function:</strong> {talent.function || talent.Funktion || 'N/A'}</span>
                      <span><strong>Location:</strong> {talent.location || 'N/A'}</span>
                      <span><strong>Status:</strong> {talent.status || 'N/A'}</span>
                      <span><strong>Email:</strong> {talent.email || talent.E_mail || 'N/A'}</span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      padding: '4px 8px', 
                      backgroundColor: assignedUsers.length > 0 ? '#28a745' : '#6c757d',
                      color: 'white',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {assignedUsers.length} User{assignedUsers.length !== 1 ? 's' : ''}
                    </div>
                    
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      ▶
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    {renderUserAssignments(talent.id)}
                    {renderCompanyRelationships(talent.id)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filteredTalents.length === 0 && !isLoading && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          <p>No talents found matching the current filters.</p>
        </div>
      )}
    </div>
  );
};

export default TalentRelationshipView; 